const LOAD = 'twineApp/announcements/LOAD';
const LOAD_SUCCESS = 'twineApp/announcements/LOAD_SUCCESS';
const LOAD_FAIL = 'twineApp/announcements/LOAD_FAIL';
const LOADPREVIEW = 'twineApp/announcements/LOADPREVIEW';
const LOADPREVIEW_SUCCESS = 'twineApp/announcements/LOADPREVIEW_SUCCESS';
const LOADPREVIEW_FAIL = 'twineApp/announcements/LOADPREVIEW_FAIL';
const LOADCOUNT = 'twineApp/announcements/LOADCOUNT';
const LOADCOUNT_SUCCESS = 'twineApp/announcements/LOADCOUNT_SUCCESS';
const LOADCOUNT_FAIL = 'twineApp/announcements/LOADCOUNT_FAIL';
const LOADACTIVE = 'twineApp/announcements/LOADACTIVE';
const LOADACTIVE_SUCCESS = 'twineApp/announcements/LOADACTIVE_SUCCESS';
const LOADACTIVE_FAIL = 'twineApp/announcements/LOADACTIVE_FAIL';
const MARKASREAD = 'twineApp/announcements/MARKASREAD';
const MARKASREAD_SUCCESS = 'twineApp/announcements/MARKASREAD_SUCCESS';
const MARKASREAD_FAIL = 'twineApp/announcements/MARKASREAD_FAIL';
const MARKALLREAD = 'twineApp/announcements/MARKALLREAD';
const MARKALLREAD_SUCCESS = 'twineApp/announcements/MARKALLREAD_SUCCESS';
const MARKALLREAD_FAIL = 'twineApp/announcements/MARKALLREAD_FAIL';
const UPDATESTATE = 'twineApp/announcements/UPDATESTATE';
const UPDATESTATE_SUCCESS = 'twineApp/announcements/UPDATESTATE_SUCCESS';
const UPDATESTATE_FAIL = 'twineApp/announcements/UPDATESTATE_FAIL';

export const PAGE_SIZE = 20;
export const PREVIEW_SIZE = 5;

// Per-user actions the API records, and the field each one sets
export const STATE_ACTIONS = {
  'dismiss-banner': 'banner_dismissed_at',
  'modal-seen': 'modal_seen_at',
  acknowledge: 'acknowledged_at',
  'cta-clicked': 'cta_clicked_at',
};

const initialState = {
  loaded: false,
  loading: false,
  error: false,
  announcements: [],
  moreAnnouncements: false,
  pagesLoaded: 0,
  tag: null,
  preview: [],
  previewLoaded: false,
  previewLoading: false,
  count: 0,
  loadedCount: false,
  active: { modal: null, banner: null },
  activeLoaded: false,
};

export const isUnread = (announcement) => !!announcement.unread;

const update = (list, id, changes) =>
  list.map((announcement) =>
    announcement.id === id ? { ...announcement, ...changes } : announcement
  );

const markRead = (list, id, readAt) =>
  list.map((announcement) =>
    announcement.id === id && isUnread(announcement)
      ? { ...announcement, unread: false, read_at: readAt }
      : announcement
  );

const markAllRead = (list, readAt) =>
  list.map((announcement) =>
    isUnread(announcement)
      ? { ...announcement, unread: false, read_at: readAt }
      : announcement
  );

const findAnnouncement = (state, id) =>
  state.announcements.find((announcement) => announcement.id === id) ||
  state.preview.find((announcement) => announcement.id === id);

const resultAnnouncements = (result) => (result && result.announcements) || [];

const hasMorePages = (result) => {
  const pagination = result && result.meta && result.meta.pagination;

  return pagination ? pagination.current_page < pagination.total_pages : false;
};

// Banners and modals that the user has acted on are no longer active
const withoutActed = (active, id, field) => ({
  modal:
    active.modal && active.modal.id === id && field === 'modal_seen_at'
      ? null
      : active.modal,
  banner:
    active.banner &&
    active.banner.id === id &&
    ['banner_dismissed_at', 'cta_clicked_at'].includes(field)
      ? null
      : active.banner,
});

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case LOAD:
      return {
        ...state,
        loading: true,
        error: false,
        tag: action.tag,
        // Switching filter starts a fresh list
        ...(action.page === 1 && action.tag !== state.tag
          ? { announcements: [], pagesLoaded: 0, loaded: false }
          : {}),
      };

    case LOAD_SUCCESS: {
      // A response for a filter the user has since switched away from
      if (action.tag !== state.tag) {
        return state;
      }

      const incoming = resultAnnouncements(action.result);
      const existing =
        action.page === 1
          ? []
          : state.announcements.filter(
              (announcement) =>
                !incoming.some((item) => item.id === announcement.id)
            );

      return {
        ...state,
        loaded: true,
        loading: false,
        announcements: existing.concat(incoming),
        moreAnnouncements: hasMorePages(action.result),
        pagesLoaded: action.page,
      };
    }

    case LOAD_FAIL:
      return action.tag !== state.tag
        ? state
        : {
            ...state,
            loading: false,
            error: true,
          };

    case LOADPREVIEW:
      return {
        ...state,
        previewLoading: true,
      };

    case LOADPREVIEW_SUCCESS:
      return {
        ...state,
        previewLoading: false,
        previewLoaded: true,
        preview: resultAnnouncements(action.result),
      };

    case LOADPREVIEW_FAIL:
      return {
        ...state,
        previewLoading: false,
        previewLoaded: true,
      };

    case LOADCOUNT_SUCCESS:
      return {
        ...state,
        loadedCount: true,
        count: action.result.count,
      };

    case LOADACTIVE_SUCCESS:
      return {
        ...state,
        activeLoaded: true,
        active: {
          modal: action.result.modal || null,
          banner: action.result.banner || null,
        },
      };

    case LOADACTIVE_FAIL:
      return {
        ...state,
        activeLoaded: true,
      };

    case MARKASREAD: {
      // Eager mark as read
      const announcement = findAnnouncement(state, action.id);

      if (!announcement || !isUnread(announcement)) {
        return state;
      }

      return {
        ...state,
        announcements: markRead(state.announcements, action.id, action.readAt),
        preview: markRead(state.preview, action.id, action.readAt),
        count: Math.max(state.count - 1, 0),
      };
    }

    case MARKALLREAD:
      // Eager mark all as read
      return {
        ...state,
        announcements: markAllRead(state.announcements, action.readAt),
        preview: markAllRead(state.preview, action.readAt),
        count: 0,
      };

    case UPDATESTATE: {
      // Eager state change, e.g. a dismissed banner disappears straight away
      const changes = { [action.field]: action.at };

      return {
        ...state,
        announcements: update(state.announcements, action.id, changes),
        preview: update(state.preview, action.id, changes),
        active: withoutActed(state.active, action.id, action.field),
      };
    }

    case LOADCOUNT:
    case LOADCOUNT_FAIL:
    case LOADACTIVE:
    case MARKASREAD_SUCCESS:
    case MARKASREAD_FAIL:
    case MARKALLREAD_SUCCESS:
    case MARKALLREAD_FAIL:
    case UPDATESTATE_SUCCESS:
    case UPDATESTATE_FAIL:
    default:
      return state;
  }
}

export function load(page, tag = null) {
  return {
    types: [LOAD, LOAD_SUCCESS, LOAD_FAIL],
    promise: (client) =>
      client.get(
        `/announcements?page=${page}&limit=${PAGE_SIZE}${
          tag ? `&tag=${tag}` : ''
        }`
      ),
    page,
    tag,
  };
}

export function loadPreview() {
  return {
    types: [LOADPREVIEW, LOADPREVIEW_SUCCESS, LOADPREVIEW_FAIL],
    promise: (client) =>
      client.get(`/announcements?page=1&limit=${PREVIEW_SIZE}`),
  };
}

export function loadCount() {
  return {
    types: [LOADCOUNT, LOADCOUNT_SUCCESS, LOADCOUNT_FAIL],
    promise: (client) => client.get('/announcements/count'),
  };
}

export function loadActive() {
  return {
    types: [LOADACTIVE, LOADACTIVE_SUCCESS, LOADACTIVE_FAIL],
    promise: (client) => client.get('/announcements/active'),
  };
}

// Each change re-syncs the count with the server once it lands. A failed
// change also reloads the preview and list to undo the eager update.
const thenReloadCount = (dispatch, getState, promise) =>
  promise.then(
    () => dispatch(loadCount()),
    () => {
      const { pagesLoaded, tag } = getState().announcements;

      if (pagesLoaded > 0) {
        dispatch(load(1, tag));
      }

      dispatch(loadPreview());
      return dispatch(loadCount());
    }
  );

export function markAsRead(id) {
  return (dispatch, getState) =>
    thenReloadCount(
      dispatch,
      getState,
      dispatch({
        types: [MARKASREAD, MARKASREAD_SUCCESS, MARKASREAD_FAIL],
        promise: (client) => client.put(`/announcements/${id}/read`),
        id,
        readAt: new Date().toISOString(),
      })
    );
}

export function markAllAsRead() {
  return (dispatch, getState) =>
    thenReloadCount(
      dispatch,
      getState,
      dispatch({
        types: [MARKALLREAD, MARKALLREAD_SUCCESS, MARKALLREAD_FAIL],
        promise: (client) => client.put('/announcements/read-all'),
        readAt: new Date().toISOString(),
      })
    );
}

/**
 * Records a per-user action (see STATE_ACTIONS). Acknowledging, clicking the
 * call to action and closing the modal also mark the announcement read.
 */
export function recordAction(id, stateAction) {
  return (dispatch, getState) => {
    const at = new Date().toISOString();
    const announcement = findAnnouncement(getState().announcements, id);

    if (stateAction !== 'dismiss-banner' && announcement) {
      dispatch({ type: MARKASREAD, id, readAt: at });
    }

    return thenReloadCount(
      dispatch,
      getState,
      dispatch({
        types: [UPDATESTATE, UPDATESTATE_SUCCESS, UPDATESTATE_FAIL],
        promise: (client) => client.put(`/announcements/${id}/${stateAction}`),
        id,
        field: STATE_ACTIONS[stateAction],
        at,
      })
    );
  };
}
